import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { ROLES, GENRES } from "@/data/options";
import { Header } from "@/components/Header";
import { TagPicker } from "@/components/TagPicker";
import { ProfileMeter } from "@/components/ProfileMeter";
import { AudioPlayer } from "@/components/AudioPlayer";
import { ShareModal } from "@/components/ShareModal";
import { ImageCropModal } from "@/components/ImageCropModal";
import { UnlockCelebration } from "@/components/UnlockCelebration";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, Plus, Camera, Check, Eye, Music2, Lock, Video, Share2, Search, Radio, Pencil, ArrowUp, ArrowDown, ArrowRight, X } from "lucide-react";

const empty = {
  name: "", bio: "", location: "", remote_friendly: false, open_to_collaborate: true, dms_enabled: true,
  roles: [], genres: [], links: { instagram: "", spotify: "", tiktok: "" }, avatar: null, gallery: [], songs: [], intro_video: null,
};

export default function ProfileBuilder() {
  const [p, setP] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [autosavedAt, setAutosavedAt] = useState(null);
  const savedSnapshot = useRef("");
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const avatarRef = useRef();
  const galleryRef = useRef();
  const songRef = useRef();
  const videoRef = useRef();
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoPct, setVideoPct] = useState(0);
  const [videoAttested, setVideoAttested] = useState(false);

  const [songModal, setSongModal] = useState(false);
  const [songTitle, setSongTitle] = useState("");
  const [songFile, setSongFile] = useState(null);
  const [attested, setAttested] = useState(false);
  const [uploadingSong, setUploadingSong] = useState(false);
  const [songPct, setSongPct] = useState(0);
  const [radioOptIn, setRadioOptIn] = useState(false);

  const toggleSongRadio = async (song) => {
    try {
      const res = await api.patch(`/profiles/me/songs/${song.id}/radio`, { opt_in: !song.radio_opt_in });
      setP((prev) => ({ ...prev, songs: prev.songs.map((s) => s.id === song.id ? { ...s, radio_opt_in: res.data.radio_opt_in } : s) }));
      toast.success(res.data.radio_opt_in ? "Added to Haus Radio" : "Removed from Haus Radio");
    } catch { toast.error("Could not update Haus Radio setting"); }
  };

  const textPayload = (prof) => ({
    name: prof.name, bio: prof.bio, location: prof.location, remote_friendly: prof.remote_friendly,
    open_to_collaborate: prof.open_to_collaborate, dms_enabled: prof.dms_enabled, roles: prof.roles, genres: prof.genres, links: prof.links,
  });

  const load = async () => {
    const res = await api.get("/profiles/me");
    const merged = { ...empty, ...res.data, dms_enabled: res.data.dms_enabled !== false, links: { ...empty.links, ...(res.data.links || {}) } };
    setP(merged);
    savedSnapshot.current = JSON.stringify({
      name: merged.name, bio: merged.bio, location: merged.location, remote_friendly: merged.remote_friendly,
      open_to_collaborate: merged.open_to_collaborate, dms_enabled: merged.dms_enabled, roles: merged.roles, genres: merged.genres, links: merged.links,
    });
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Autosave text fields 2.5s after the last edit — nothing is lost if "Save" is never clicked.
  useEffect(() => {
    if (loading) return;
    const cur = JSON.stringify(textPayload(p));
    if (cur === savedSnapshot.current) { setDirty(false); return; }
    setDirty(true);
    const t = setTimeout(async () => {
      try {
        await api.put("/profiles/me", textPayload(p));
        savedSnapshot.current = cur;
        setDirty(false);
        setAutosavedAt(Date.now());
      } catch { /* silent — manual save surfaces errors */ }
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [p, loading]);

  // Warn before leaving the page with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setLink = (k, v) => setP((prev) => ({ ...prev, links: { ...prev.links, [k]: v } }));

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusable = el.matches("input,textarea") ? el : el.querySelector("input,textarea,button");
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 450);
    el.classList.add("meter-flash");
    setTimeout(() => el.classList.remove("meter-flash"), 1400);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profiles/me", textPayload(p));
      savedSnapshot.current = JSON.stringify(textPayload(p));
      setDirty(false);
      setAutosavedAt(Date.now());
      await refresh();
      if (user?.has_browse_access) {
        toast.success("You're live in the Haus!", {
          description: "Your card is visible to every member in the Directory.",
          action: { label: "See it", onClick: () => navigate("/directory") },
          duration: 6000,
        });
      } else {
        toast.success("You're live in the Haus! Your card is now visible to every member.");
      }
      maybeCelebrate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const shareMyCard = () => { if (user) setShareOpen(true); };

  const [unlockOpen, setUnlockOpen] = useState(false);
  const maybeCelebrate = () => {
    if (!user || user.has_browse_access) return;
    const key = `wh_unlock_prompt_${user.user_id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    setUnlockOpen(true);
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const togglePublish = async () => {
    try {
      const res = await api.post("/profiles/me/publish", { is_published: !p.is_published });
      setP((prev) => ({ ...prev, is_published: res.data.is_published }));
      toast.success(res.data.is_published ? "Your card is live again." : "Your card is now hidden.");
      if (res.data.is_published) maybeCelebrate();
    } catch { toast.error("Could not update visibility"); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account/me", { data: { confirm: confirmText } });
      toast.success("Your account has been deleted. Take care out there.");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const checkSize = (file, maxMB, kind) => {
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`That ${kind} is ${(file.size / 1048576).toFixed(1)} MB — the limit is ${maxMB} MB. Try a smaller version.`);
      return false;
    }
    return true;
  };

  const [cropTask, setCropTask] = useState(null); // { file, target: "avatar" | "gallery" }

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!checkSize(file, 10, "image")) return;
    setCropTask({ file, target: "avatar" });
  };

  const uploadGallery = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!checkSize(file, 10, "image")) return;
    setCropTask({ file, target: "gallery" });
  };

  const uploadCropped = async (file) => {
    const target = cropTask.target;
    setCropTask(null);
    try {
      const f = new FormData();
      f.append("file", file);
      const res = await api.post(`/profiles/me/${target}`, f, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 });
      if (target === "avatar") { set("avatar", res.data); toast.success("Avatar updated"); }
      else { set("gallery", [...p.gallery, res.data]); toast.success("Image added"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed — check the file type (JPG/PNG/WebP) and your connection"); }
  };

  const delGallery = async (id) => {
    await api.delete(`/profiles/me/gallery/${id}`);
    set("gallery", p.gallery.filter((g) => g.id !== id));
  };

  const readDuration = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
    v.onerror = () => { URL.revokeObjectURL(url); reject(); };
    v.src = url;
  });

  const uploadIntroVideo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["mp4", "mov"].includes(ext)) { toast.error("Please use an MP4 or MOV file"); e.target.value = ""; return; }
    if (!checkSize(file, 60, "video")) { e.target.value = ""; return; }
    try {
      const dur = await readDuration(file);
      if (dur && dur > 15.9) { toast.error("Intro video must be 15 seconds or less"); e.target.value = ""; return; }
    } catch {
      // Some browsers can't read .mov metadata — allow upload rather than block.
    }
    setUploadingVideo(true);
    setVideoPct(0);
    try {
      const f = new FormData(); f.append("file", file); f.append("attested", "true");
      const res = await api.post("/profiles/me/intro-video", f, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
        onUploadProgress: (ev) => setVideoPct(Math.min(100, Math.round((ev.loaded * 100) / (ev.total || 1)))),
      });
      set("intro_video", res.data);
      setVideoAttested(false);
      toast.success("Intro video added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingVideo(false); e.target.value = ""; }
  };

  const delIntroVideo = async () => {
    await api.delete("/profiles/me/intro-video");
    set("intro_video", null);
  };

  const submitSong = async () => {
    if (!songFile) return toast.error("Choose an MP3 file");
    if (!attested) return toast.error("You must confirm the NO-AI attestation");
    if (!checkSize(songFile, 30, "MP3")) return;
    setUploadingSong(true);
    setSongPct(0);
    try {
      const f = new FormData();
      f.append("file", songFile);
      f.append("title", songTitle || songFile.name.replace(/\.mp3$/i, ""));
      f.append("attested", "true");
      f.append("radio_opt_in", radioOptIn ? "true" : "false");
      const res = await api.post("/profiles/me/songs", f, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
        onUploadProgress: (ev) => setSongPct(Math.min(100, Math.round((ev.loaded * 100) / (ev.total || 1)))),
      });
      set("songs", [...p.songs, res.data]);
      setSongModal(false); setSongTitle(""); setSongFile(null); setAttested(false); setRadioOptIn(false);
      toast.success("Song published");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingSong(false); }
  };

  const delSong = async (id) => {
    await api.delete(`/profiles/me/songs/${id}`);
    set("songs", p.songs.filter((s) => s.id !== id));
  };

  const [editingSongId, setEditingSongId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const saveRename = async () => {
    try {
      const res = await api.patch(`/profiles/me/songs/${editingSongId}`, { title: editTitle });
      setP((prev) => ({ ...prev, songs: prev.songs.map((s) => s.id === editingSongId ? { ...s, title: res.data.title } : s) }));
      setEditingSongId(null);
      toast.success("Song renamed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  const moveSong = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= p.songs.length) return;
    const arr = [...p.songs];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setP((prev) => ({ ...prev, songs: arr }));
    try { await api.post("/profiles/me/songs/reorder", { order: arr.map((s) => s.id) }); }
    catch { toast.error("Could not save the new order"); }
  };

  if (loading) {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><span className="font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Loading…</span></div>;
  }

  const avatarUrl = mediaUrl(p.avatar);

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-24 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Your HausCard</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter uppercase text-cream mt-2">Build your HausCard</h1>
            <p className="text-ash mt-2 text-sm">Quality over quantity — three songs, your best work. Publishes instantly.</p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate("/directory")} data-testid="search-haus-btn" className="inline-flex items-center gap-2 border border-gold text-gold font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-gold hover:text-ink transition-colors">
                <Search size={15} /> Search the Haus
              </button>
              <button onClick={() => user && navigate(`/profile/${user.user_id}`)} data-testid="view-public-btn" className="inline-flex items-center gap-2 border border-line text-cream font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-gold hover:text-gold transition-colors">
                <Eye size={15} /> Preview
              </button>
              <button onClick={shareMyCard} data-testid="share-my-card-btn" className="inline-flex items-center gap-2 border border-line text-cream font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-gold hover:text-gold transition-colors">
                <Share2 size={15} /> Share
              </button>
              <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors disabled:opacity-60">
                <Check size={15} /> {saving ? "Saving…" : "Save & publish"}
              </button>
            </div>
            <span data-testid="autosave-status" className={`font-mono text-[10px] uppercase tracking-wide ${dirty ? "text-ash animate-pulse" : "text-ash/60"}`}>
              {dirty ? "Unsaved changes — autosaving…" : autosavedAt ? "All changes saved" : ""}
            </span>
          </div>
        </div>

        {!user?.has_browse_access && (
          <div className="border border-gold/40 bg-gold/5 p-5 mb-10 flex items-start gap-3">
            <Lock size={18} className="text-gold mt-0.5 shrink-0" />
            <p className="text-sm text-ash">Your card is live. To browse and connect with other creators, <button onClick={() => navigate("/paywall")} className="text-gold hover:underline">unlock browsing ($8/mo or $59/yr)</button>.</p>
          </div>
        )}

        {user?.has_browse_access && p.is_published && (
          <div data-testid="live-confirmation-banner" className="border border-gold/40 bg-gold/5 p-5 mb-10 flex items-center gap-3 flex-wrap">
            <span className="w-2.5 h-2.5 rounded-full bg-gold animate-pulse shrink-0" aria-hidden />
            <p className="text-sm text-cream flex-1">You're live in the Haus — your card is visible to every member in the Directory.</p>
            <button onClick={() => navigate("/directory")} data-testid="live-banner-directory-btn" className="inline-flex items-center gap-1.5 text-gold font-mono text-[11px] uppercase tracking-widest hover:underline underline-offset-4">
              See your card <ArrowRight size={13} />
            </button>
          </div>
        )}

        <ProfileMeter profile={p} onJump={jumpTo} />

        <div className="grid lg:grid-cols-[300px_1fr] gap-px bg-line border border-line">
          {/* Avatar + media */}
          <div className="bg-surface p-6 flex flex-col gap-6">
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-3 block">Avatar</label>
              <div id="meter-avatar" className="aspect-square bg-ink border border-line relative overflow-hidden group">
                {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-line"><Camera size={40} /></div>}
                <button onClick={() => avatarRef.current.click()} data-testid="upload-avatar-btn" className="absolute inset-0 bg-ink/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-gold font-mono text-xs uppercase tracking-widest">
                  <Upload size={16} className="mr-2" /> Change
                </button>
              </div>
              <input ref={avatarRef} type="file" accept="image/*" hidden onChange={uploadAvatar} />
            </div>

            <div>
              <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-3 block">Gallery ({p.gallery.length}/2)</label>
              <div id="meter-gallery" className="grid grid-cols-2 gap-3">
                {p.gallery.map((g) => (
                  <div key={g.id} className="aspect-square bg-ink border border-line relative group">
                    <img src={mediaUrl(g.media)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => delGallery(g.id)} data-testid={`del-gallery-${g.id}`} className="absolute top-1 right-1 bg-ink/80 text-cream p-1 hover:text-gold"><Trash2 size={14} /></button>
                  </div>
                ))}
                {p.gallery.length < 2 && (
                  <button onClick={() => galleryRef.current.click()} data-testid="upload-gallery-btn" className="aspect-square border border-dashed border-line flex items-center justify-center text-ash hover:border-gold hover:text-gold transition-colors">
                    <Plus size={22} />
                  </button>
                )}
              </div>
              <input ref={galleryRef} type="file" accept="image/*" hidden onChange={uploadGallery} />
            </div>
          </div>

          {/* Details */}
          <div className="bg-surface p-6 md:p-8 flex flex-col gap-7">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-2 block">Name</label>
                <input id="profile-name-input" data-testid="profile-name-input" value={p.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-ink border border-line text-cream px-4 py-3 focus:outline-none focus:border-gold transition-colors" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-2 block">Location</label>
                <input id="profile-location-input" data-testid="profile-location-input" value={p.location} onChange={(e) => set("location", e.target.value)} placeholder="City, Country" className="w-full bg-ink border border-line text-cream px-4 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-ash/50" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash block">Bio</label>
                <span data-testid="bio-char-count" className={`font-mono text-[11px] ${(p.bio?.length || 0) > 500 ? "text-destructive" : "text-ash"}`}>{p.bio?.length || 0}/500</span>
              </div>
              <textarea id="profile-bio-input" data-testid="profile-bio-input" value={p.bio} onChange={(e) => set("bio", e.target.value)} rows={3} maxLength={500} placeholder="What do you make, and who are you looking for?" className="w-full bg-ink border border-line text-cream px-4 py-3 focus:outline-none focus:border-gold transition-colors resize-none placeholder:text-ash/50" />
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" data-testid="open-collab-toggle" checked={p.open_to_collaborate} onChange={(e) => set("open_to_collaborate", e.target.checked)} className="w-5 h-5 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
                <span className="font-mono text-xs uppercase tracking-wide text-cream">Open to collaborate</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" data-testid="remote-toggle" checked={p.remote_friendly} onChange={(e) => set("remote_friendly", e.target.checked)} className="w-5 h-5 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
                <span className="font-mono text-xs uppercase tracking-wide text-cream">Remote friendly</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer" title="When off, nobody can message you and you can't send messages.">
                <input type="checkbox" data-testid="dms-toggle" checked={p.dms_enabled} onChange={(e) => set("dms_enabled", e.target.checked)} className="w-5 h-5 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
                <span className="font-mono text-xs uppercase tracking-wide text-cream">Direct messages {p.dms_enabled ? "on" : "off"}</span>
              </label>
            </div>

            <div id="meter-roles"><TagPicker label="Roles" options={ROLES} selected={p.roles} onChange={(v) => set("roles", v)} testidPrefix="role" /></div>
            <div id="meter-genres"><TagPicker label="Genres" options={GENRES} selected={p.genres} onChange={(v) => set("genres", v)} testidPrefix="genre" /></div>

            <div id="meter-links">
              <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-3 block">Links</label>
              <p className="font-mono text-[10px] uppercase tracking-wide text-ash/60 mb-3">Paste the full link or just your handle — both work. For Spotify, paste your full artist link.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {["instagram", "spotify", "tiktok"].map((k) => (
                  <input key={k} data-testid={`link-${k}-input`} value={p.links[k]} onChange={(e) => setLink(k, e.target.value)} placeholder={k === "spotify" ? "Spotify artist link" : k[0].toUpperCase() + k.slice(1) + " handle or link"} className="bg-ink border border-line text-cream px-3 py-2.5 text-sm focus:outline-none focus:border-gold transition-colors placeholder:text-ash/50" />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Intro video */}
        <div id="meter-video" className="mt-px border border-line border-t-0 bg-surface p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash block">Intro video — 15s max, MP4 or MOV</label>
              <p className="text-sm text-ash mt-1">A short, personal hello. This is what makes a card feel human.</p>
            </div>
            {p.intro_video && (
              <button onClick={delIntroVideo} data-testid="del-intro-video-btn" className="shrink-0 inline-flex items-center gap-2 border border-line text-ash font-mono text-xs uppercase tracking-widest px-4 py-2.5 hover:border-destructive hover:text-destructive transition-colors">
                <Trash2 size={15} /> Remove
              </button>
            )}
          </div>
          <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,.mp4,.mov" hidden onChange={uploadIntroVideo} />
          {p.intro_video ? (
            <video src={mediaUrl(p.intro_video)} controls playsInline data-testid="intro-video-preview" className="w-full max-w-xl aspect-video bg-ink border border-line" />
          ) : (
            <div>
              <label className="flex gap-3 items-start mb-4 cursor-pointer">
                <input type="checkbox" data-testid="video-noai-attestation-checkbox" checked={videoAttested} onChange={(e) => setVideoAttested(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
                <span className="text-sm text-ash leading-relaxed">
                  I attest this video is human-made and contains no AI-generated video, voice, or music. I understand WriterHaus enforces a strict NO-AI policy and violations are removed.
                </span>
              </label>
              <button onClick={() => videoRef.current.click()} disabled={uploadingVideo || !videoAttested} data-testid="add-intro-video-btn" className="w-full border border-dashed border-line text-ash hover:border-gold hover:text-gold transition-colors py-12 flex flex-col items-center gap-3 disabled:opacity-50 disabled:hover:border-line disabled:hover:text-ash disabled:cursor-not-allowed">
                <Video size={28} />
                <span className="font-mono text-xs uppercase tracking-wide">{uploadingVideo ? `Uploading… ${videoPct}%` : videoAttested ? "Upload a 15-second intro" : "Confirm the NO-AI attestation first"}</span>
              </button>
            </div>
          )}
        </div>

        {/* Songs */}
        <div id="meter-songs" className="mt-px border border-line border-t-0 bg-surface p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash">Songs ({p.songs.length}/3) — MP3 only</label>
            {p.songs.length < 3 && (
              <button onClick={() => setSongModal(true)} data-testid="add-song-btn" className="inline-flex items-center gap-2 border border-gold text-gold font-mono text-xs uppercase tracking-widest px-4 py-2.5 hover:bg-gold hover:text-ink transition-colors">
                <Plus size={15} /> Add song
              </button>
            )}
          </div>
          {p.songs.length === 0 ? (
            <div className="border border-dashed border-line p-10 text-center text-ash">
              <Music2 size={32} className="mx-auto mb-3 text-line" />
              <p className="text-sm">Upload up to three MP3s. Lead with your best.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {p.songs.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2">
                  <div className="flex flex-col shrink-0">
                    <button onClick={() => moveSong(i, -1)} disabled={i === 0} data-testid={`song-up-${i}`} className="text-ash hover:text-gold disabled:opacity-25 transition-colors p-0.5"><ArrowUp size={14} /></button>
                    <button onClick={() => moveSong(i, 1)} disabled={i === p.songs.length - 1} data-testid={`song-down-${i}`} className="text-ash hover:text-gold disabled:opacity-25 transition-colors p-0.5"><ArrowDown size={14} /></button>
                  </div>
                  {editingSongId === s.id ? (
                    <div className="flex-1 flex items-center gap-2 border border-gold bg-ink px-3 py-2.5">
                      <input
                        data-testid={`song-title-input-${i}`}
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditingSongId(null); }}
                        autoFocus
                        className="flex-1 bg-transparent text-cream text-sm focus:outline-none"
                      />
                      <button onClick={saveRename} data-testid={`song-title-save-${i}`} className="text-gold hover:text-gold-hi p-1"><Check size={16} /></button>
                      <button onClick={() => setEditingSongId(null)} className="text-ash hover:text-cream p-1"><X size={16} /></button>
                    </div>
                  ) : (
                    <div className="flex-1"><AudioPlayer song={s} index={i} /></div>
                  )}
                  <button onClick={() => { setEditingSongId(s.id); setEditTitle(s.title); }} data-testid={`rename-song-${i}`} title="Rename this song" className="shrink-0 w-11 h-11 flex items-center justify-center border border-line text-ash hover:border-gold hover:text-gold transition-colors"><Pencil size={15} /></button>
                  <button
                    onClick={() => toggleSongRadio(s)}
                    data-testid={`radio-toggle-${i}`}
                    title={s.radio_opt_in ? "Playing on Haus Radio — click to remove" : "Not on Haus Radio — click to include"}
                    className={`shrink-0 w-11 h-11 flex items-center justify-center border transition-colors ${s.radio_opt_in ? "border-gold text-gold" : "border-line text-ash hover:border-gold hover:text-gold"}`}
                  ><Radio size={16} /></button>
                  <button onClick={() => delSong(s.id)} data-testid={`del-song-${i}`} className="shrink-0 w-11 h-11 flex items-center justify-center border border-line text-ash hover:border-destructive hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
              <p className="font-mono text-[10px] uppercase tracking-wide text-ash/60 mt-1 flex items-center gap-1.5"><Radio size={11} /> Gold radio icon = song plays on Haus Radio · pencil renames · arrows reorder.</p>
            </div>
          )}
        </div>
      </div>

      {/* Visibility & account */}
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 pb-20">
        <div className="border border-line bg-surface p-6 md:p-8 flex flex-col gap-4">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-cream">Card visibility & account</h2>
          {p.slug && (
            <div className="flex flex-wrap items-center justify-between gap-4 border border-line bg-ink p-4">
              <div>
                <p className="text-cream text-sm font-medium">Your personal link</p>
                <p className="font-mono text-xs text-gold mt-1" data-testid="vanity-url">{window.location.origin.replace(/^https?:\/\//, "")}/{p.slug}</p>
              </div>
              <button
                onClick={async () => { await navigator.clipboard.writeText(`${window.location.origin}/${p.slug}`); toast.success("Personal link copied"); }}
                data-testid="copy-vanity-btn"
                className="border border-line text-cream font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-gold hover:text-gold transition-colors"
              >Copy link</button>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4 border border-line bg-ink p-4">
            <div>
              <p className="text-cream text-sm font-medium">{p.is_published ? "Your card is live" : "Your card is hidden"}</p>
              <p className="text-ash text-xs mt-1">{p.is_published ? "Visible in the directory, radio and public pages." : "Invisible everywhere until you publish again — nothing is deleted."}</p>
            </div>
            <button onClick={togglePublish} data-testid="toggle-publish-btn" className="border border-line text-cream font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-gold hover:text-gold transition-colors">
              {p.is_published ? "Hide my card" : "Publish my card"}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 border border-destructive/40 bg-ink p-4">
            <div>
              <p className="text-cream text-sm font-medium">Delete my account</p>
              <p className="text-ash text-xs mt-1">Permanently removes your card, songs, photos, video and login. This cannot be undone.</p>
            </div>
            <button onClick={() => { setConfirmText(""); setDeleteOpen(true); }} data-testid="delete-account-btn" className="border border-destructive text-destructive font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-destructive hover:text-cream transition-colors">
              Delete account…
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirm modal */}
      {deleteOpen && (
        <div className="fixed inset-0 z-[70] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setDeleteOpen(false)}>
          <div className="bg-surface border border-destructive/50 max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-bold uppercase text-cream tracking-tight mb-2">Delete your account?</h3>
            <p className="text-ash text-sm leading-relaxed mb-5">Your HausCard, songs, photos, intro video and login will be permanently removed. Messages you sent will remain in other members' inboxes, shown as "Deleted member".</p>
            <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-2 block">Type DELETE to confirm</label>
            <input data-testid="delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="w-full bg-ink border border-line text-cream px-4 py-3 mb-5 focus:outline-none focus:border-destructive placeholder:text-ash/40" />
            <div className="flex gap-3">
              <button onClick={() => setDeleteOpen(false)} className="flex-1 border border-line text-cream font-mono text-xs uppercase tracking-widest py-3 hover:border-gold transition-colors">Cancel</button>
              <button onClick={deleteAccount} disabled={confirmText !== "DELETE" || deleting} data-testid="delete-confirm-btn" className="flex-1 bg-destructive text-cream font-bold uppercase tracking-widest text-xs py-3 hover:opacity-90 transition-opacity disabled:opacity-40">
                {deleting ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Song upload modal with NO-AI attestation */}
      {songModal && (
        <div className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setSongModal(false)}>
          <div className="bg-surface border border-line max-w-lg w-full p-7" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-bold uppercase text-cream tracking-tight mb-1">Add a song</h3>
            <p className="font-mono text-[11px] uppercase tracking-wide text-ash mb-6">MP3 only · max 30MB</p>

            <input data-testid="song-title-input" value={songTitle} onChange={(e) => setSongTitle(e.target.value)} placeholder="Song title" className="w-full bg-ink border border-line text-cream px-4 py-3 mb-4 focus:outline-none focus:border-gold transition-colors placeholder:text-ash/50" />

            <button onClick={() => songRef.current.click()} data-testid="choose-song-file-btn" className="w-full border border-dashed border-line text-ash hover:border-gold hover:text-gold transition-colors py-6 flex flex-col items-center gap-2 mb-5">
              <Upload size={22} />
              <span className="font-mono text-xs uppercase tracking-wide">{songFile ? songFile.name : "Choose MP3 file"}</span>
            </button>
            <input ref={songRef} type="file" accept=".mp3,audio/mpeg" hidden onChange={(e) => setSongFile(e.target.files[0])} />

            <label className="flex items-start gap-3 cursor-pointer border border-line p-4 mb-3 bg-ink">
              <input type="checkbox" data-testid="noai-attestation-checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
              <span className="font-mono text-[11px] leading-relaxed text-ash uppercase tracking-wide">
                I attest this recording is human-made and contains no AI-generated music. I understand WriterHaus enforces a strict NO-AI policy and violations are removed.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer border border-line p-4 mb-6 bg-ink">
              <input type="checkbox" data-testid="radio-optin-checkbox" checked={radioOptIn} onChange={(e) => setRadioOptIn(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
              <span className="font-mono text-[11px] leading-relaxed text-ash uppercase tracking-wide">
                Include this song in Haus Radio — the members-only shuffle station. Optional, and you can change it anytime.
              </span>
            </label>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setSongModal(false)} className="font-mono text-xs uppercase tracking-widest text-ash hover:text-cream px-5 py-3">Cancel</button>
              <button onClick={submitSong} disabled={uploadingSong || !attested} data-testid="submit-song-btn" className="bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors disabled:opacity-50">
                {uploadingSong ? `Uploading… ${songPct}%` : "Publish song"}
              </button>
            </div>
          </div>
        </div>
      )}
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} url={user ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${user.user_id}` : ""} storyUrl={user ? `${process.env.REACT_APP_BACKEND_URL}/api/og/story/${user.user_id}.png` : ""} title="My WriterHaus HausCard" text="Hear my songs on WriterHaus — let's write together." />
      <UnlockCelebration open={unlockOpen} onClose={() => setUnlockOpen(false)} />
      {cropTask && (
        <ImageCropModal
          file={cropTask.file}
          aspect={1}
          title={cropTask.target === "avatar" ? "Position your profile photo" : "Position your photo"}
          onDone={uploadCropped}
          onCancel={() => setCropTask(null)}
        />
      )}
    </div>
  );
}
